import { createSlice, createSelector } from "@reduxjs/toolkit";

const initialState = {
  selectedCategory: null,
  activeQuestionId: null,
  currentResponseIndex: 0,
  activeFilters: {
    demographics: {
      age_group: [],
      gender: [],
      education: [],
      income: [],
    },
    tags: [],
    dateFilter: {
      type: null, // 'range' or 'month'
      startDate: null,
      endDate: null,
      months: [], // e.g., ['2024-11', '2024-12']
    },
  },
  status: "idle",
  error: null,
};

const civicPulseSlice = createSlice({
  name: "civicPulse",
  initialState,
  reducers: {
    setSelectedCategory: (state, action) => {
      if (!action.payload) {
        state.selectedCategory = null;
        state.activeQuestionId = null;
        state.currentResponseIndex = 0;
        return;
      }
      const { questionId, category } = action.payload;
      // If clicking the same category in the same treemap, deselect it
      if (
        state.activeQuestionId === questionId &&
        state.selectedCategory &&
        state.selectedCategory.category === category
      ) {
        state.selectedCategory = null;
        state.activeQuestionId = null;
      } else {
        state.selectedCategory = { questionId, category };
        state.activeQuestionId = questionId;
      }
      state.currentResponseIndex = 0;
    },
    setCurrentResponseIndex: (state, action) => {
      state.currentResponseIndex = action.payload;
    },
    toggleFilter: (state, action) => {
      const { type, value } = action.payload;
      const filterArray = state.activeFilters[type];
      const index = filterArray.indexOf(value);

      if (index === -1) {
        filterArray.push(value);
      } else {
        filterArray.splice(index, 1);
      }
    },
    clearFilters: (state) => {
      state.activeFilters = initialState.activeFilters;
    },
    toggleDemographicFilter: (state, action) => {
      const { category, value } = action.payload;
      const group = state.activeFilters.demographics[category];
      if (!group) return;
      const index = group.indexOf(value);
      if (index === -1) {
        group.push(value);
      } else {
        group.splice(index, 1);
      }
    },
    setDateFilter: (state, action) => {
      state.activeFilters.dateFilter = action.payload;
    },
    clearDateFilter: (state) => {
      state.activeFilters.dateFilter = {
        type: null,
        startDate: null,
        endDate: null,
        months: [],
      };
    },
  },
});

export const {
  setSelectedCategory,
  setCurrentResponseIndex,
  toggleFilter,
  clearFilters,
  toggleDemographicFilter,
  setDateFilter,
  clearDateFilter,
} = civicPulseSlice.actions;

// Basic selectors
export const selectCivicPulseState = (state) => state.civicPulse;

// Memoized selectors for active filters
const selectDemographics = (state) => state.civicPulse.activeFilters.demographics;
const selectTags = (state) => state.civicPulse.activeFilters.tags;
const selectDateFilter = (state) => state.civicPulse.activeFilters.dateFilter;

// Memoized selector to prevent unnecessary re-renders and API calls
// Only creates new object reference when filter values actually change
export const selectActiveFilters = createSelector(
  [selectDemographics, selectTags, selectDateFilter],
  (demographics, tags, dateFilter) => ({
    demographics,
    tags,
    dateFilter
  })
);


export default civicPulseSlice.reducer;